import { Head, Link, router } from "@inertiajs/react"
import {
  Clock,
  Database,
  Download,
  Edit,
  Eye,
  FolderArchive,
  HardDrive,
  Play,
} from "lucide-react"
import { useState } from "react"

import Heading from "@/components/heading"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import AppLayout from "@/layouts/app-layout"
import { formatBytes } from "@/lib/utils"
import {
  allBackupsPath,
  appBackupPath,
  appBackupsPath,
  appPath,
  downloadAppBackupPath,
  editBackupConfigPath,
  serversPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { App, Backup, BackupConfig } from "@/types"

type BackupWithConfig = Backup & {
  backup_config: Pick<
    BackupConfig,
    "id" | "backup_type" | "destination_type" | "source_paths"
  >
}

type BackupWithApp = BackupWithConfig & {
  app?: Pick<App, "id" | "name" | "db_type" | "db_name"> & {
    server?: Pick<{ id: number; name: string }, "id" | "name">
  }
}

interface BackupsIndexProps {
  app: Pick<App, "id" | "name" | "db_type" | "db_name"> | null
  backups: BackupWithApp[]
  backup_configs: Pick<BackupConfig, "id" | "backup_type" | "enabled" | "source_paths" | "destination_path" | "destination_type">[]
}

type FrequencyFilter = "all" | "daily" | "weekly" | "monthly" | "manual"

export default function BackupsIndex({
  app,
  backups,
  backup_configs,
}: BackupsIndexProps) {
  const [filter, setFilter] = useState<FrequencyFilter>("all")
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null)

  const isAllBackupsView = app === null
  const breadcrumbs: BreadcrumbItem[] = isAllBackupsView
    ? [
        { title: "Servers", href: serversPath() },
        { title: "All Backups", href: allBackupsPath() },
      ]
    : [
        { title: "Servers", href: serversPath() },
        { title: app.name, href: appPath(app.id) },
        { title: "Backups", href: appBackupsPath(app.id) },
      ]

  const filteredBackups =
    filter === "all"
      ? backups
      : backups.filter((b) => b.frequency_type === filter)

  function openConfirmDialog(configId: number) {
    setSelectedConfigId(configId)
    setConfirmDialogOpen(true)
  }

  function triggerManualBackup() {
    if (selectedConfigId && app) {
      router.post(appBackupsPath(app.id), {
        backup_config_id: selectedConfigId,
      })
      setConfirmDialogOpen(false)
      setSelectedConfigId(null)
    }
  }

  const selectedConfig = backup_configs.find((c) => c.id === selectedConfigId)

  function formatDate(date: string | null | undefined): string {
    if (!date) return "N/A"
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  function getStatusVariant(status: string) {
    switch (status) {
      case "success":
        return "default"
      case "error":
        return "destructive"
      case "running":
        return "outline"
      default:
        return "secondary"
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={isAllBackupsView ? "All Backups" : `Backups - ${app.name}`} />

      <div className="p-6">
        <div className="flex items-center justify-between">
          <Heading
            title={isAllBackupsView ? "All Backups" : `Backups for ${app.name}`}
            description={
              isAllBackupsView
                ? "100 most recent backups across all servers and applications"
                : "View and manage backup history"
            }
            marginBottom={false}
          />
        </div>

        {/* Manual Backup Section - only when viewing a single app */}
        {!isAllBackupsView && backup_configs.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Run manual backup</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {backup_configs
                .filter((c) => c.enabled)
                .map((config) => (
                  <div
                    key={config.id}
                    className="flex items-start justify-between gap-4 rounded-lg border p-4"
                  >
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        {config.backup_type === "database" ? (
                          <Database className="text-muted-foreground h-5 w-5" />
                        ) : (
                          <FolderArchive className="text-muted-foreground h-5 w-5" />
                        )}
                        <p className="font-medium capitalize">
                          {config.backup_type}
                        </p>
                        <Badge variant="secondary" className="text-xs">
                          {config.destination_type}
                        </Badge>
                      </div>
                      
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <div>
                          <span className="font-medium">Source:</span>{" "}
                          {config.backup_type == "directory" && config.source_paths && config.source_paths.length > 0 && (
                            config.source_paths.join(", ")
                          )}
                          {config.backup_type == "database" && app && (
                            <span>{app.db_name} ({app.db_type})</span>
                          )}
                        </div>
                        {config.destination_path && (
                          <div>
                            <span className="font-medium">Destination:</span>{" "}
                            {config.destination_path}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                      >
                        <Link href={editBackupConfigPath(config.id)}>
                          <Edit className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openConfirmDialog(config.id)}
                      >
                        <Play className="mr-2 h-4 w-4" />
                        Run
                      </Button>
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>
        )}

        {/* Confirmation Dialog - only when viewing a single app */}
        {!isAllBackupsView && (
        <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Manual Backup</DialogTitle>
              <DialogDescription>
                Are you sure you want to run a manual backup?
                {selectedConfig && (
                  <div className="mt-3 space-y-1 rounded-md bg-muted p-3 text-sm">
                    <div>
                      <span className="font-medium">Type:</span> {selectedConfig.backup_type}
                    </div>
                    <div>
                      <span className="font-medium">Destination:</span> {selectedConfig.destination_type}
                    </div>
                    {selectedConfig.source_paths && selectedConfig.source_paths.length > 0 && (
                      <div>
                        <span className="font-medium">Source:</span>{" "}
                        {selectedConfig.source_paths.join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={triggerManualBackup}>
                <Play className="mr-2 h-4 w-4" />
                Run Backup
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}

        {/* Filter Tabs */}
        <div className="mt-6 flex gap-2 border-b pb-4">
          {(
            ["all", "daily", "weekly", "monthly", "manual"] as FrequencyFilter[]
          ).map((freq) => (
            <button
              key={freq}
              onClick={() => setFilter(freq)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filter === freq
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {freq.charAt(0).toUpperCase() + freq.slice(1)}
            </button>
          ))}
        </div>

        {/* Backups List */}
        <div className="mt-6 p-2">
          {filteredBackups.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No backups found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    {isAllBackupsView && (
                      <th className="pb-3 text-left font-medium">App</th>
                    )}
                    <th className="pb-3 text-left font-medium">Type</th>
                    <th className="pb-3 text-left font-medium">Frequency</th>
                    <th className="pb-3 text-left font-medium">Status</th>
                    <th className="pb-3 text-left font-medium">Size</th>
                    <th className="pb-3 text-left font-medium">Completed</th>
                    <th className="pb-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBackups.map((backup) => {
                    const appId = app?.id ?? backup.app?.id ?? backup.app_id
                    return (
                      <tr key={backup.id} className="border-b last:border-0">
                        {isAllBackupsView && (
                          <td className="py-3">
                            {backup.app ? (
                              <Link
                                href={appPath(backup.app.id)}
                                className="font-medium hover:underline"
                              >
                                {backup.app.server
                                  ? `${backup.app.name} (${backup.app.server.name})`
                                  : backup.app.name}
                              </Link>
                            ) : (
                              "—"
                            )}
                          </td>
                        )}
                        <td className="py-3">
                          <div className="flex flex-col gap-0.5">
                            <span className="flex items-center gap-2">
                              {backup.backup_config?.backup_type ===
                              "database" ? (
                                <Database className="text-muted-foreground h-4 w-4" />
                              ) : (
                                <FolderArchive className="text-muted-foreground h-4 w-4" />
                              )}
                              {backup.backup_config?.backup_type ?? "—"}
                            </span>
                            {(backup.backup_config?.backup_type ===
                              "database"
                              ? (app?.db_name && app?.db_type) ||
                                (backup.app?.db_name && backup.app?.db_type)
                              : backup.backup_config?.source_paths?.length) && (
                              <span className="text-muted-foreground text-xs">
                                {backup.backup_config?.backup_type ===
                                "database"
                                  ? `${app?.db_name ?? backup.app?.db_name ?? ""} (${app?.db_type ?? backup.app?.db_type ?? ""})`
                                  : backup.backup_config?.source_paths?.join(
                                      ", ",
                                    )}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 capitalize">
                          {backup.frequency_type}
                        </td>
                        <td className="py-3">
                          <Badge variant={getStatusVariant(backup.status)}>
                            {backup.status}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <span className="flex items-center gap-1">
                            <HardDrive className="h-3 w-3" />
                            {formatBytes(backup.file_size)}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(backup.completed_at)}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex justify-end gap-2">
                            {appId && (
                              <>
                                <Button variant="ghost" size="icon" asChild>
                                  <Link
                                    href={appBackupPath(appId, backup.id)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Link>
                                </Button>
                                {backup.status === "success" &&
                                  backup.file_path && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      asChild
                                    >
                                      <a
                                        href={downloadAppBackupPath(
                                          appId,
                                          backup.id,
                                        )}
                                      >
                                        <Download className="h-4 w-4" />
                                      </a>
                                    </Button>
                                  )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
